import { CommonModule } from '@angular/common';
import { Component, inject, NgZone } from '@angular/core';
import { BraintreeService, ProviderName } from '@fgb/core';
import { BaseExpressCheckoutComponent, EXPRESS_CHECKOUT_SETTINGS, ExpressCheckoutSettings } from '../../base-express-checkout';
import { PipesModule } from '@fgb/portal-component-library/src/lib/pipes';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Braintree PayPal express checkout provider (PayPal Web SDK v6). The base class creates the
 * Braintree client + PayPal V6 instance and loads the SDK; this component then offers a button that,
 * on click, starts the PayPal V6 payment session. When the buyer authorises, Braintree returns a
 * nonce which is posted to the backend and the result emitted up to the container.
 *
 * V6 (unlike v1) has no `paypal.Buttons(...).render()`: `session.start()` must be triggered from the
 * user's click, so the flow begins in onStartCheckout(), not in a post-init hook.
 */
@Component({
  selector: 'fgbcl-paypal-express-checkout',
  standalone: true,
  imports: [CommonModule, PipesModule, TranslateModule],
  templateUrl: './paypal.component.html',
  styleUrl: './paypal.component.scss',
  providers: [
    {
      provide: EXPRESS_CHECKOUT_SETTINGS,
      useValue: (settings: any): ExpressCheckoutSettings => {
        return {
          settings: {
            authorization: settings.clientToken,
          },
        };
      },
    },
  ],
})
export class PaypalExpressCheckoutComponent extends BaseExpressCheckoutComponent {
  private paypalService: BraintreeService;
  private zone = inject(NgZone);

  constructor() {
    const paypalService = inject(BraintreeService);
    super(ProviderName.PayPal, paypalService);
    this.paypalService = paypalService;
  }

  /* ================================
   * Template handlers
   * ================================ */

  /**
   * Start the PayPal V6 payment session. Bound to the button click so `session.start()` runs inside
   * the user's gesture (V6 needs transient activation). Guards on ready/processing/context.
   */
  async onStartCheckout(): Promise<void> {
    if (!this.ready || this.processing || !this.context) {
      return;
    }

    // Show the loading state from the moment the button is pressed, through the PayPal popup and
    // authorisation. completePurchase() keeps it on for the backend submit; cancel/error clear it.
    this.processing = true;

    await this.paypalService.startCheckout(this.context, {
      onAuthorised: (nonce) => this.zone.run(() => this.completePurchase(nonce)),
      onCancelled: () =>
        this.zone.run(() => {
          this.processing = false;
          this.incomplete.emit({ providerId: ProviderName.PayPal, success: false });
        }),
      onError: (error: any) =>
        this.zone.run(() => {
          this.processing = false;
          this.result.emit({ providerId: ProviderName.PayPal, success: false, error: { message: error?.message } });
        }),
    });
  }

  /* ================================
   * Private Helpers
   * ================================ */

  /** Complete the express purchase with the nonce returned by the PayPal authorisation. */
  private async completePurchase(nonce: string): Promise<void> {
    if (!this.context) {
      return;
    }
    // processing is already true from onStartCheckout(); keep it on through the backend submit.
    this.processing = true;
    try {
      const submitted = await this.paypalService.submitNonce(nonce, this.context);
      this.result.emit({ providerId: ProviderName.PayPal, success: submitted, data: this.context.orderId });
    } catch (error: any) {
      this.logService.error('PayPal express checkout failed:', error);
      this.result.emit({ providerId: ProviderName.PayPal, success: false, error: { message: error?.message } });
    } finally {
      this.processing = false;
    }
  }
}
